/**
 * GrossProgressionChart Component
 *
 * Line chart showing gross progression by working day.
 * Displays current year, last year, and 3-year average lines.
 */

import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { DailyGrossSummary, DailyGrossEntry, DepartmentGrossValues, ProjectionTargetData, UnprocessedDateTotals, DailyGrossSnapshot } from '../../types/forecast';
import { DepartmentKey, getDepartmentLabel } from './DepartmentTabs';

interface GrossProgressionChartProps {
  currentYear: number;
  currentMonthData: DailyGrossSummary | null;
  historical: Record<number, DailyGrossSummary | null>;
  currentWorkingDay: number;
  /** Total working days in the current year's month (used to limit trend line) */
  currentYearTotalWorkingDays?: number;
  selectedDepartment?: DepartmentKey;
  projectionTargets?: ProjectionTargetData;
  /** Unprocessed deals by date for adding to current year line */
  unprocessedByDate?: Record<string, UnprocessedDateTotals>;
  /** Trending end value from the tile (ensures chart trend line matches tile) */
  trendingEndValue?: number | null;
  /** Final month gross for last year from DOC_GROSS_SUMMARY (ensures grey line endpoint matches tile) */
  lastYearFinalGross?: number | null;
  /** Final month weighted average from DOC_GROSS_SUMMARY (ensures green line endpoint matches tile) */
  averageFinalGross?: number | null;
  /** Blue tile's combined gross (processed + unprocessed) at current working day.
   *  When provided, the chart snaps the blue line at currentWorkingDay to this value. */
  currentYearGrossAtPoint?: number | null;
  /** Nightly snapshots of blue tile values for past working days (accurate chart history) */
  dailySnapshots?: DailyGrossSnapshot[];
}

interface ChartDataPoint {
  workingDay: number;
  [key: string]: number | null;
}

const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
        <p className="font-semibold text-gray-900 mb-2">Working Day {label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-600">{entry.name}:</span>
            <span className="font-medium" style={{ color: entry.color }}>
              {entry.value !== null ? formatCurrency(entry.value) : '—'}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// Helper to get gross value based on department selection
const getGrossValue = (entry: DailyGrossEntry, department: DepartmentKey): number => {
  if (department === 'ALL') {
    // "All" department includes ALL departments including OTHERS
    const deptGross = entry.cumulativeDepartmentGross;
    if (!deptGross) return entry.cumulativeGross;
    return (deptGross.NEW_VEHICLE || 0) +
           (deptGross.USED_VEHICLE || 0) +
           (deptGross.SERVICE || 0) +
           (deptGross.PARTS || 0) +
           (deptGross.BODY_SHOP || 0) +
           (deptGross.RENTAL || 0) +
           (deptGross.OTHERS || 0);
  }
  const deptKey = department as keyof DepartmentGrossValues;
  return entry.cumulativeDepartmentGross?.[deptKey] ?? 0;
};

// Helper to get unprocessed gross for a department
const getUnprocessedGross = (
  totals: UnprocessedDateTotals,
  department: DepartmentKey
): number => {
  if (department === 'ALL') {
    // Only NEW and USED have unprocessed deals
    return totals.totalGross;
  } else if (department === 'NEW_VEHICLE') {
    return totals.newVehicleGross;
  } else if (department === 'USED_VEHICLE') {
    return totals.usedVehicleGross;
  }
  // Other departments (SERVICE, PARTS, BODY_SHOP, RENTAL) don't have unprocessed deals
  return 0;
};

export const GrossProgressionChart: React.FC<GrossProgressionChartProps> = ({
  currentYear,
  currentMonthData,
  historical,
  currentWorkingDay,
  currentYearTotalWorkingDays,
  selectedDepartment = 'ALL',
  projectionTargets,
  unprocessedByDate,
  trendingEndValue,
  lastYearFinalGross,
  averageFinalGross,
  currentYearGrossAtPoint,
  dailySnapshots,
}) => {
  // Build chart data by aligning years by working day number
  const chartData = useMemo(() => {
    const data: ChartDataPoint[] = [];

    // Determine max working days across all years for data range
    let maxWorkingDaysFromData = 0;

    if (currentMonthData) {
      maxWorkingDaysFromData = Math.max(maxWorkingDaysFromData, currentMonthData.totalWorkingDays);
    }

    Object.values(historical).forEach((summary) => {
      if (summary) {
        maxWorkingDaysFromData = Math.max(maxWorkingDaysFromData, summary.totalWorkingDays);
      }
    });

    // Cap at department-specific working days when provided (e.g., SERVICE has fewer days than NEW_VEHICLE)
    const maxWorkingDays = currentYearTotalWorkingDays
      ? Math.min(maxWorkingDaysFromData, currentYearTotalWorkingDays)
      : maxWorkingDaysFromData;

    // Build data points for each working day
    // NOTE: DAILY_GROSS_SUMMARY now includes all sold deals (processed + unprocessed)
    // via the unified COALESCE query, so no separate unprocessed overlay is needed.
    for (let wd = 1; wd <= maxWorkingDays; wd++) {
      const point: ChartDataPoint = { workingDay: wd };

      // Current year — gross already includes all sold deals
      if (currentMonthData) {
        const entry = currentMonthData.dailyEntries.find(
          (e) => e.workingDayNumber === wd
        );
        if (entry) {
          point[currentYear.toString()] = getGrossValue(entry, selectedDepartment);
        } else {
          point[currentYear.toString()] = null;
        }
      }

      // Historical years
      Object.entries(historical).forEach(([year, summary]) => {
        if (summary) {
          const entry = summary.dailyEntries.find(
            (e: DailyGrossEntry) => e.workingDayNumber === wd
          );
          point[year] = entry ? getGrossValue(entry, selectedDepartment) : null;
        }
      });

      data.push(point);
    }

    // Overlay nightly snapshot values for past working days (accurate chart history)
    // Snapshots capture the exact blue tile value each night, avoiding double-counting
    // from unprocessed deals that get processed later.
    if (dailySnapshots && dailySnapshots.length > 0) {
      const deptKey = selectedDepartment === 'ALL' ? 'ALL' : selectedDepartment;
      for (const snap of dailySnapshots) {
        // Only apply to past working days (not today — today uses live value)
        if (snap.workingDay < currentWorkingDay) {
          const point = data.find(p => p.workingDay === snap.workingDay);
          if (point && snap.grossByDepartment[deptKey as keyof typeof snap.grossByDepartment] !== undefined) {
            point[currentYear.toString()] = snap.grossByDepartment[deptKey as keyof typeof snap.grossByDepartment];
          }
        }
      }
    }

    // Snap current year's blue line at the current working day to match the tile value exactly
    if (currentYearGrossAtPoint != null && currentWorkingDay > 0) {
      const targetPoint = data.find(p => p.workingDay === currentWorkingDay);
      if (targetPoint) {
        targetPoint[currentYear.toString()] = currentYearGrossAtPoint;
      }
    }

    return data;
  }, [currentMonthData, historical, currentYear, selectedDepartment, unprocessedByDate, currentYearTotalWorkingDays, currentYearGrossAtPoint, currentWorkingDay, dailySnapshots]);

  // Calculate 3-year average line, projection targets, and blended trend projection
  const chartDataWithAverage = useMemo(() => {
    const historicalYears = Object.keys(historical).map(Number);

    // First pass: calculate 3-year average for all points
    const dataWithAvg: ChartDataPoint[] = chartData.map((point) => {
      const validValues = historicalYears
        .map((year) => point[year.toString()])
        .filter((v): v is number => v !== null && v !== undefined);

      const avg = validValues.length > 0
        ? validValues.reduce((sum, v) => sum + v, 0) / validValues.length
        : null;

      // Add projection target if available
      let projectionTarget: number | null = null;
      if (projectionTargets?.dailyTargets) {
        const target = projectionTargets.dailyTargets.find(
          (t) => t.workingDayNumber === point.workingDay
        );
        projectionTarget = target?.cumulativeTarget ?? null;
      }

      return { ...point, '3-Yr Avg': avg, 'Projection': projectionTarget };
    });

    // Second pass: calculate trend projection for future days
    // If trendingEndValue is provided (from the tile), we interpolate from current actual to that end value
    // This ensures the chart trend line matches the tile's trending number
    if (currentWorkingDay > 0 && currentMonthData) {
      // Get current actual at today
      const currentDayData = dataWithAvg.find(p => p.workingDay === currentWorkingDay);
      const currentActual = currentDayData?.[currentYear.toString()] as number | null;

      // Determine max working day for trend line - use current year's total, not historical max
      const trendMaxDay = currentYearTotalWorkingDays ?? currentMonthData.totalWorkingDays;

      // Use the provided trendingEndValue if available, otherwise fall back to chart-calculated value
      let endValue = trendingEndValue;
      if (endValue === null || endValue === undefined) {
        // Fallback: calculate using simple Time-Weighted Blend with chart's 3-yr avg
        const lastDayData = dataWithAvg.find(p => p.workingDay === trendMaxDay);
        const historicalAvgAtMonthEnd = lastDayData?.['3-Yr Avg'] as number | null;
        if (currentActual && currentActual > 0 && trendMaxDay > 0 && historicalAvgAtMonthEnd) {
          const currentPace = (currentActual / currentWorkingDay) * trendMaxDay;
          const weight = currentWorkingDay / trendMaxDay;
          endValue = Math.round((currentPace * weight) + (historicalAvgAtMonthEnd * (1 - weight)));
        }
      }

      if (currentActual && currentActual > 0 && trendMaxDay > 0 && endValue) {
        // Linear interpolation from current actual to trending end value
        // This ensures the chart line ends at exactly the tile's trending number
        const daysRemaining = trendMaxDay - currentWorkingDay;
        const grossToAdd = endValue - currentActual;

        dataWithAvg.forEach((point) => {
          if (point.workingDay > currentWorkingDay && point.workingDay <= trendMaxDay) {
            // Linear interpolation: how far along are we from current day to end?
            const daysFromCurrent = point.workingDay - currentWorkingDay;
            const progressRatio = daysFromCurrent / daysRemaining;
            const trendAtThisDay = currentActual + (grossToAdd * progressRatio);
            point['Trend'] = Math.round(trendAtThisDay);

            // Null out current year data for future days so solid line stops at today
            point[currentYear.toString()] = null;
          } else if (point.workingDay > trendMaxDay) {
            // Beyond current year's working days - no trend line
            point['Trend'] = null;
            point[currentYear.toString()] = null;
          } else if (point.workingDay === currentWorkingDay) {
            // Connect trend line to actual at current day
            point['Trend'] = currentActual;
          } else {
            point['Trend'] = null;
          }
        });
      }
    }

    // Third pass: snap last year and 3-yr avg endpoints to tile values (DOC_GROSS_SUMMARY)
    // This ensures the chart line endpoints match the "Final month" values shown in tiles
    if (dataWithAvg.length > 0) {
      const lastPoint = dataWithAvg[dataWithAvg.length - 1];
      const lastYearKey = (currentYear - 1).toString();
      if (lastYearFinalGross != null && lastPoint[lastYearKey] != null) {
        lastPoint[lastYearKey] = Math.round(lastYearFinalGross);
      }
      if (averageFinalGross != null && lastPoint['3-Yr Avg'] != null) {
        lastPoint['3-Yr Avg'] = Math.round(averageFinalGross);
      }
    }

    return dataWithAvg;
  }, [chartData, historical, projectionTargets, currentWorkingDay, currentMonthData, currentYear, currentYearTotalWorkingDays, trendingEndValue, lastYearFinalGross, averageFinalGross]);

  // Determine which lines to show
  const historicalYears = Object.keys(historical)
    .map(Number)
    .filter((year) => historical[year] !== null)
    .sort((a, b) => b - a);

  const lineColors: Record<string, string> = {
    [currentYear.toString()]: '#2563eb', // Blue-600
    [(currentYear - 1).toString()]: '#9ca3af', // Gray-400
    [(currentYear - 2).toString()]: '#d1d5db', // Gray-300
    [(currentYear - 3).toString()]: '#e5e7eb', // Gray-200
    '3-Yr Avg': '#10b981', // Emerald-500
    'Projection': '#9333ea', // Purple-600
    'Trend': '#2563eb', // Blue-600 (same as current year, but will be dashed)
  };

  const hasData = chartDataWithAverage.length > 0;

  if (!hasData) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Gross Progression</h3>
        <div className="h-80 flex items-center justify-center text-gray-500">
          No progression data available
        </div>
      </div>
    );
  }

  const chartTitle = selectedDepartment === 'ALL'
    ? 'Gross Progression by Working Day'
    : `${getDepartmentLabel(selectedDepartment)} - Gross Progression by Working Day`;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{chartTitle}</h3>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartDataWithAverage}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="workingDay"
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              label={{
                value: 'Working Day',
                position: 'insideBottom',
                offset: -5,
                fontSize: 12,
                fill: '#6b7280',
              }}
            />
            <YAxis
              tickFormatter={formatCurrency}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              width={80}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              formatter={(value) => (
                <span className="text-sm text-gray-600">{value}</span>
              )}
            />

            {/* Current working day reference line */}
            {currentWorkingDay > 0 && (
              <ReferenceLine
                x={currentWorkingDay}
                stroke="#2563eb"
                strokeDasharray="5 5"
                strokeWidth={2}
                label={{
                  value: 'Today',
                  position: 'top',
                  fill: '#2563eb',
                  fontSize: 11,
                }}
              />
            )}

            {/* Current Year Line (solid, prominent) */}
            {currentMonthData && (
              <Line
                type="monotone"
                dataKey={currentYear.toString()}
                name={currentYear.toString()}
                stroke={lineColors[currentYear.toString()]}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
              />
            )}

            {/* Trend Line - Blended projection from current day (dashed blue) */}
            {currentMonthData && currentWorkingDay > 0 && (
              <Line
                type="monotone"
                dataKey="Trend"
                name="Trend"
                stroke={lineColors['Trend']}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls={false}
              />
            )}

            {/* Last Year Line (dashed) */}
            {historicalYears.includes(currentYear - 1) && (
              <Line
                type="monotone"
                dataKey={(currentYear - 1).toString()}
                name={(currentYear - 1).toString()}
                stroke={lineColors[(currentYear - 1).toString()]}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                activeDot={{ r: 4 }}
              />
            )}

            {/* 3-Year Average Line (dotted, green) */}
            {historicalYears.length > 0 && (
              <Line
                type="monotone"
                dataKey="3-Yr Avg"
                name="3-Yr Avg"
                stroke={lineColors['3-Yr Avg']}
                strokeWidth={2}
                strokeDasharray="2 2"
                dot={false}
                activeDot={{ r: 4 }}
              />
            )}

            {/* Projection Target Line (solid, purple) */}
            {projectionTargets && projectionTargets.dailyTargets.length > 0 && (
              <Line
                type="monotone"
                dataKey="Projection"
                name="Projection"
                stroke={lineColors['Projection']}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
